// Helper del flujo de conexión de ARCHIVOS de Google Drive (scope drive.file):
//   1) GIS code-popup  -> authorization code (con el client_id del proyecto Drive)
//   2) el backend intercambia el code por tokens y devuelve un access_token de corta vida
//   3) Google Picker (access_token + developer key + app_id) -> archivos elegidos (multi-select)
// Con drive.file el backend puede LEER los archivos que el usuario seleccionó individualmente (sin
// Service Account). Las libs de Google se cargan dinámicamente (no están en el bundle del frontend).

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GAPI_SRC = 'https://apis.google.com/js/api.js';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
// Red de seguridad: si el navegador bloquea el popup de OAuth, GIS puede no disparar callback ni
// error_callback → la promesa quedaba colgada y el modal trabado (busy sin salida). Con el timeout,
// rechaza con 'timeout' (no es señal de cancelación) → el wizard muestra error y se destraba.
const AUTH_CODE_TIMEOUT_MS = 120000;

const _scriptPromises = {};

function loadScript(src) {
    if (_scriptPromises[src]) return _scriptPromises[src];
    _scriptPromises[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            delete _scriptPromises[src];
            reject(new Error('No se pudo cargar ' + src));
        };
        document.body.appendChild(script);
    });
    return _scriptPromises[src];
}

// Pide el authorization code con el code-popup de GIS (redirect_uri implícito = 'postmessage').
export async function requestDriveAuthCode(clientId) {
    await loadScript(GIS_SRC);
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (fn, arg) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            fn(arg);
        };
        const timer = setTimeout(() => finish(reject, new Error('timeout')), AUTH_CODE_TIMEOUT_MS);
        const codeClient = window.google.accounts.oauth2.initCodeClient({
            client_id: clientId,
            scope: DRIVE_SCOPE,
            ux_mode: 'popup',
            callback: (response) => {
                if (response && response.code) finish(resolve, response.code);
                else finish(reject, new Error(response && response.error ? response.error : 'no_code'));
            },
            error_callback: (err) => finish(reject, new Error(err && err.type ? err.type : 'oauth_error')),
        });
        codeClient.requestCode();
    });
}

// Abre el Picker de ARCHIVOS (multi-select, filtrado por mime types soportados). Resuelve a un array
// de {id, name, mimeType, sizeBytes, url}, o null si el usuario cancela. El filtro de mime hace que el
// usuario sólo pueda elegir tipos que sabemos procesar; el backend igual re-valida server-side.
export async function pickDriveFiles({accessToken, developerKey, appId, mimeTypes}) {
    await loadScript(GAPI_SRC);
    await new Promise((resolve, reject) => {
        window.gapi.load('picker', {callback: resolve, onerror: () => reject(new Error('picker_load_error'))});
    });
    const picker = window.google.picker;
    // Una vista "Mi unidad" + una "Compartidos conmigo": con drive.file el usuario puede sumar
    // archivos que NO son suyos (la planilla del cliente suele pertenecer a otra persona) y el
    // backend igual los lee con el token del usuario. Sin la 2da vista, un archivo compartido no
    // aparecía como seleccionable → el usuario creía que "no puede subirlo por no ser propietario".
    const buildDocsView = (ownedByMe) => {
        const v = new picker.DocsView(picker.ViewId.DOCS)
            .setIncludeFolders(false)
            .setSelectFolderEnabled(false)
            .setOwnedByMe(ownedByMe);
        if (mimeTypes && mimeTypes.length) v.setMimeTypes(mimeTypes.join(','));
        return v;
    };
    return new Promise((resolve) => {
        const builder = new picker.PickerBuilder()
            .setOAuthToken(accessToken)
            .setDeveloperKey(developerKey)
            .enableFeature(picker.Feature.MULTISELECT_ENABLED)
            .addView(buildDocsView(true))
            .addView(buildDocsView(false))
            .setCallback((data) => {
                if (data.action === picker.Action.PICKED) {
                    const docs = (data.docs || []).map((d) => ({
                        id: d.id, name: d.name, mimeType: d.mimeType,
                        sizeBytes: d.sizeBytes, url: d.url,
                    }));
                    resolve(docs.length ? docs : null);
                } else if (data.action === picker.Action.CANCEL) {
                    resolve(null);
                }
            });
        if (appId) builder.setAppId(String(appId));
        builder.build().setVisible(true);
    });
}
